/**
 * storage.js
 * LocalStorage operations for persisting application state
 */

import { state } from './state.js?v=302';

// Storage keys
export const STORAGE_KEYS = {
  CHATS: "chatbot_chats",
  CURRENT_CHAT: "chatbot_current_chat",
  THEME: "chatbot_theme",
  SELECTED_THEME: "chatbot_selected_theme",
  SIDEBAR_STATE: "chatbot_sidebar_state",
  MODEL: "chatbot_model",
  FONT_TYPE: "edFontType",
  FONT_SIZE: "edFontSize",
  SYSTEM_PROMPTS: "chatbot_system_prompts",
  SYSTEM_PROMPT_ID: "chatbot_system_prompt_id",
  SYSTEM_MESSAGE: "chatbot_system_message",
};

/**
 * Save chats array to localStorage
 * Auto-prunes oldest chats if storage quota is exceeded
 */
export function saveChats() {
  const maxRetries = 10;
  let retries = 0;
  let chatsToSave = [...state.chats];
  
  while (retries < maxRetries) {
    try {
      localStorage.setItem(STORAGE_KEYS.CHATS, JSON.stringify(chatsToSave));
      // Update state if we pruned any chats
      if (chatsToSave.length < state.chats.length) {
        const pruned = state.chats.length - chatsToSave.length;
        state.chats = chatsToSave;
        console.log(`[Storage] Pruned ${pruned} old chat(s) to free up space`);
      }
      return; // Success
    } catch (e) {
      if (e.name === 'QuotaExceededError' && chatsToSave.length > 1) {
        // Remove the oldest chat (first in array, excluding current)
        const currentChatId = localStorage.getItem(STORAGE_KEYS.CURRENT_CHAT);
        const oldestIndex = chatsToSave.findIndex(c => c.id !== currentChatId);
        if (oldestIndex !== -1) {
          chatsToSave.splice(oldestIndex, 1);
          retries++;
        } else {
          // Only current chat remains, truncate its messages
          if (chatsToSave[0] && chatsToSave[0].messages.length > 10) {
            chatsToSave[0].messages = chatsToSave[0].messages.slice(-10);
            retries++;
          } else {
            throw e; // Can't prune further
          }
        }
      } else {
        throw e; // Different error or can't prune
      }
    }
  }
}

/**
 * Save current chat ID to localStorage
 * @param {string|null} chatId - Chat ID to save, or null to remove
 */
export function saveCurrentChatId(chatId) {
  if (chatId) {
    localStorage.setItem(STORAGE_KEYS.CURRENT_CHAT, chatId);
  } else {
    localStorage.removeItem(STORAGE_KEYS.CURRENT_CHAT);
  }
}

/**
 * Save theme preference to localStorage
 * @param {string} theme - Theme name ('light' or 'dark')
 */
export function saveTheme(theme) {
  localStorage.setItem(STORAGE_KEYS.THEME, theme);
}

/**
 * Save selected theme variant to localStorage
 * @param {string} themeVariant - Theme variant name
 */
export function saveSelectedTheme(themeVariant) {
  localStorage.setItem(STORAGE_KEYS.SELECTED_THEME, themeVariant);
}

/**
 * Save sidebar state to localStorage
 * @param {boolean} isExpanded - Whether sidebar is expanded
 */
export function saveSidebarState(isExpanded) {
  localStorage.setItem(
    STORAGE_KEYS.SIDEBAR_STATE,
    isExpanded ? "expanded" : "collapsed"
  );
}

/**
 * Save model selection to localStorage
 * @param {string} model - Model identifier
 */
export function saveModel(model) {
  localStorage.setItem(STORAGE_KEYS.MODEL, model);
}

/**
 * Save font type preference to localStorage
 * @param {string} fontType - Font family name
 */
export function saveFontType(fontType) {
  localStorage.setItem(STORAGE_KEYS.FONT_TYPE, fontType);
}

/**
 * Save font size preference to localStorage
 * @param {string} fontSize - Font size value
 */
export function saveFontSize(fontSize) {
  localStorage.setItem(STORAGE_KEYS.FONT_SIZE, fontSize);
}

/**
 * Get font type preference from localStorage
 * @returns {string|null} Saved font type or null
 */
export function getFontType() {
  return localStorage.getItem(STORAGE_KEYS.FONT_TYPE);
}

/**
 * Get font size preference from localStorage
 * @returns {string|null} Saved font size or null
 */
export function getFontSize() {
  return localStorage.getItem(STORAGE_KEYS.FONT_SIZE);
}

/**
 * Save draft message for a specific chat
 * @param {string} chatId - Chat ID (or 'default' for new chat)
 * @param {string} value - Draft message content
 */
export function saveDraft(chatId, value) {
  const draftKey = `chatbot_draft_${chatId || 'default'}`;
  if (value.trim()) {
    localStorage.setItem(draftKey, value);
  } else {
    localStorage.removeItem(draftKey);
  }
}

/**
 * Get draft message for a specific chat
 * @param {string} chatId - Chat ID (or 'default' for new chat)
 * @returns {string} Draft message content
 */
export function getDraft(chatId) {
  const draftKey = `chatbot_draft_${chatId || 'default'}`;
  return localStorage.getItem(draftKey) || '';
}

/**
 * Remove draft message for a specific chat
 * @param {string} chatId - Chat ID (or 'default' for new chat)
 */
export function removeDraft(chatId) {
  const draftKey = `chatbot_draft_${chatId || 'default'}`;
  localStorage.removeItem(draftKey);
}

/**
 * Clear all draft messages (convenience function)
 * Removes drafts for both default and any stored chatId
 */
export function clearDraft() {
  // Clear default draft
  localStorage.removeItem('chatbot_draft_default');
  // Also clear current chat draft from state if available
  const currentChatId = localStorage.getItem('chatbot_current_chat');
  if (currentChatId) {
    localStorage.removeItem(`chatbot_draft_${currentChatId}`);
  }
}

/**
 * Prune chats older than maxAgeDays from state and localStorage.
 * Keeps the currently active chat regardless of age.
 * @param {number} maxAgeDays - Max age in days (default: 3)
 * @returns {number} Number of chats pruned
 */
export function pruneOldChats(maxAgeDays = 3) {
  const maxAgeMs = maxAgeDays * 24 * 60 * 60 * 1000;
  const now = Date.now();
  const currentChatId = localStorage.getItem(STORAGE_KEYS.CURRENT_CHAT);
  const before = state.chats.length;

  state.chats = state.chats.filter(chat => {
    // Never prune the active conversation
    if (chat.id === currentChatId) return true;

    // Never prune pinned conversations
    if (chat.pinned) return true;

    // Determine age from createdAt or id (which is Date.now() as string)
    let chatTime;
    if (chat.createdAt) {
      chatTime = new Date(chat.createdAt).getTime();
    } else {
      chatTime = parseInt(chat.id, 10);
    }

    if (isNaN(chatTime)) return true; // keep if we can't determine age
    return (now - chatTime) < maxAgeMs;
  });

  const pruned = before - state.chats.length;
  if (pruned > 0) {
    saveChats();
    console.log(`[Storage] Pruned ${pruned} conversation(s) older than ${maxAgeDays} days`);
  }
  return pruned;
}

/**
 * Clean up deprecated storage keys
 * Call this during initialization to remove old/unused keys
 */
export function cleanupDeprecatedKeys() {
  const deprecatedKeys = [
    "openrouter_key",
    "chatbot_system_prompts",
    "chatbot_system_prompt_id",
    "chatbot_system_message",
  ];

  deprecatedKeys.forEach(key => {
    localStorage.removeItem(key);
  });
}
